import { Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UserData } from "../models/user-data";
import { BehaviorSubject, map } from "rxjs";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
	/**
	 *
	 * @param router
	 * @param route
	 */
	constructor(
		private router: Router,
		private route: ActivatedRoute,
	) {}

	private userDataSubject: BehaviorSubject<UserData> = new BehaviorSubject((() => {
		const sessionId = localStorage.getItem('sessionId');
		const functionalGroups = localStorage.getItem('functionalGroups');
		// Clear incomplete sessions so the login redirect token is consumed fresh
		if (sessionId && functionalGroups === null) {
			localStorage.removeItem('sessionId');
			localStorage.removeItem('userName');
			localStorage.removeItem('companyName');
			localStorage.removeItem('firstName');
			localStorage.removeItem('lastName');
			localStorage.removeItem('functionalGroups');
			localStorage.removeItem('analyzer_selected_environment');
			return { sessionID: null, userName: '', isLoggedIn: false, companyName: '', firstName: '', lastName: '', functionalGroups: [] } as unknown as UserData;
		}
		return {
			sessionID: sessionId,
			userName: localStorage.getItem('userName'),
			isLoggedIn: !!sessionId,
			companyName: localStorage.getItem('companyName'),
			firstName: localStorage.getItem('firstName'),
			lastName: localStorage.getItem('lastName'),
			functionalGroups: functionalGroups?.split(';'),
		} as UserData;
	})());

	public userData$ = this.userDataSubject.asObservable();

	public isLoggedIn$ = this.userData$.pipe(map(userData => !!userData.sessionID || !!localStorage.getItem('sessionId')));

	public startedRedirect = false;

	public sessionID$ = this.userData$.pipe(map(userData => userData.sessionID ?? localStorage.getItem('sessionId')));

	/**
	 * Clears all session data and redirects to login.
	 */
	public clearSession(): void {
		this.userDataSubject.next({ userName: '', sessionID: '', isLoggedIn: false, companyName: '', firstName: '', lastName: '', functionalGroups: [] });

		// Clear data from local storage
		localStorage.removeItem('sessionId');
		localStorage.removeItem('userName');
		localStorage.removeItem('companyName');
		localStorage.removeItem('firstName');
		localStorage.removeItem('lastName');
		localStorage.removeItem('functionalGroups');
		localStorage.removeItem('analyzer_selected_environment');
	}

	/**
	 * Sets new user session data.
	 * @param userData New user data.
	 */
	public updateSession(userData: UserData) {
		localStorage.setItem('sessionId', userData.sessionID);
		localStorage.setItem('userName', userData.userName);
		localStorage.setItem('companyName', userData.companyName);
		localStorage.setItem('firstName', userData.firstName);
		localStorage.setItem('lastName', userData.lastName);
		localStorage.setItem('functionalGroups', userData.functionalGroups.join(';'));

		userData.isLoggedIn = !!userData.sessionID;

		const newUserData = {
			userName: userData.userName,
			sessionID: userData.sessionID,
			isLoggedIn: !!userData.sessionID,
			companyName: userData.companyName,
			email: userData.email,
			firstName: userData.firstName,
			lastName: userData.lastName,
			functionalGroups: userData.functionalGroups,
		};
		this.userDataSubject.next(newUserData);
	}

	/**
	 * Redirects to external authentication page.
	 *
	 * CRITICAL: Clears all query parameters from URL before encoding to prevent
	 * token accumulation on subsequent redirects. The auth service will add a
	 * fresh one-time token when redirecting back.
	 */
	public login() {
		// Clean ALL query params from URL before encoding
		const url = new URL(window.location.href);
		url.search = ''; // Clear all query parameters

		const host = encodeURIComponent(url.toString());
		const loginUrl = `${environment.redirectUrl}?origin=${host}`;

		document.location.href = loginUrl;
	}

	/**
	 * Removes specific query parameters from the URL without page reload.
	 * Used to clean up authentication tokens after successful login.
	 *
	 * @param paramsToRemove Array of parameter names to remove from URL
	 */
	public removeSpecificQueryParams(paramsToRemove: string[]) {
		const queryParams = { ...this.route.snapshot.queryParams };

		// Remove specified parameters
		paramsToRemove.forEach(param => delete queryParams[param]);

		// Navigate to cleaned URL
		const urlTree = this.router.createUrlTree([], { queryParams });
		this.router.navigateByUrl(urlTree);
	}
}